/* Perplexity Sonar Pro vault — API-key + live signaal-probe. */
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound, Link2, CheckCircle2, Loader2, Eye, EyeOff, Search } from "lucide-react";
import { SubSection } from "./VaultBits";

/* ----------------------------------------------------------------- */
/*  Perplexity vault — api_key + ping + dry-run signal probe         */
/* ----------------------------------------------------------------- */
function PerplexityVault({ cfg, onSaved }) {
    const { t } = useTranslation();
    const [apiKey, setApiKey] = useState("");
    const [reveal, setReveal] = useState(false);
    const [busy, setBusy] = useState(false);
    const [pingBusy, setPingBusy] = useState(false);
    const [pingResult, setPingResult] = useState(null);
    const [probeBusy, setProbeBusy] = useState(false);
    const [probe, setProbe] = useState({ target_sector: "FinTech", target_country: "BE", target_role: "CTO" });
    const [probeResult, setProbeResult] = useState(null);

    const save = async () => {
        if (!apiKey.trim() && !cfg.configured) { toast.error(t("integr.enter_key")); return; }
        setBusy(true);
        try {
            await api.patch("/integrations/perplexity", { api_key: apiKey });
            toast.success(t("integr.key_saved", { name: "Perplexity" }));
            setApiKey(""); setReveal(false); onSaved();
        } catch (e) { toast.error(e?.response?.data?.detail || t("integr.save_failed")); }
        finally { setBusy(false); }
    };
    const remove = async () => {
        if (!window.confirm(t("integr.delete_key_confirm", { name: "Perplexity" }))) return;
        setBusy(true);
        try { await api.delete("/integrations/perplexity"); toast.success(t("integr.deleted")); setPingResult(null); setProbeResult(null); onSaved(); }
        catch (e) { toast.error(e?.response?.data?.detail || t("integr.delete_failed")); }
        finally { setBusy(false); }
    };
    const testPing = async () => {
        setPingBusy(true);
        try {
            const r = await api.get("/integrations/perplexity/test");
            setPingResult(r.data);
            toast.success(t("integr.pplx_valid"));
        } catch (e) { setPingResult(null); toast.error(e?.response?.data?.detail || t("integr.test_failed")); }
        finally { setPingBusy(false); }
    };
    const runProbe = async () => {
        setProbeBusy(true);
        try {
            const r = await api.post("/integrations/perplexity/probe", { ...probe, limit: 2 });
            setProbeResult(r.data.signals || []);
            toast.success(t("integr.pplx_probe_done", { n: r.data.count }));
        } catch (e) { setProbeResult(null); toast.error(e?.response?.data?.detail || t("integr.probe_failed")); }
        finally { setProbeBusy(false); }
    };
    const hostOf = (u) => { try { return new URL(u).hostname.replace(/^www\./, ""); } catch { return u; } };

    return (
        <div className="space-y-4">
            <SubSection title={`1. ${t("integr.sec_api_key")}`} testid="perplexity-credentials">
                <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">Perplexity API Key</label>
                <div className="mt-1.5 flex gap-2">
                    <div className="flex-1 relative">
                        <KeyRound size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--jr-text-3)]" />
                        <input value={apiKey} onChange={(e) => setApiKey(e.target.value)}
                            placeholder={cfg.api_key_masked || t("integr.pplx_key_ph")}
                            type={reveal ? "text" : "password"}
                            data-testid="perplexity-apikey-input"
                            className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg pl-9 pr-10 py-2.5 text-sm text-white outline-none focus:border-[var(--jr-brand)]" />
                        <button type="button" onClick={() => setReveal((r) => !r)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-[var(--jr-text-3)] hover:text-white">
                            {reveal ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                    </div>
                    <button onClick={save} disabled={busy} data-testid="perplexity-save"
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[var(--jr-brand)] text-black text-sm font-medium hover:bg-[var(--jr-brand-2)] disabled:opacity-50">
                        {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} {t("common.save")}
                    </button>
                </div>
                <p className="mt-2 text-[10px] text-[var(--jr-text-3)]">
                    {t("integr.pplx_where")} <span className="text-[var(--jr-text-2)]">perplexity.ai → Settings → API</span>.
                    {" "}{t("integr.pplx_pricing")}
                </p>
                {cfg.configured && (
                    <button onClick={remove} className="mt-2 text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)] hover:text-[#ef4444]">
                        {t("integr.delete_current_key")}
                    </button>
                )}
            </SubSection>

            <SubSection title={`2. ${t("integr.sec_test_conn")}`}
                action={
                    <button onClick={testPing} disabled={pingBusy || !cfg.configured} data-testid="perplexity-test"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-mono uppercase tracking-[0.16em] bg-[var(--jr-brand)]/15 text-[var(--jr-brand)] border border-[var(--jr-brand)]/30 hover:bg-[var(--jr-brand)]/25 disabled:opacity-50">
                        {pingBusy ? <Loader2 size={10} className="animate-spin" /> : <CheckCircle2 size={10} />} Ping sonar-pro
                    </button>
                }
                testid="perplexity-test-section">
                {!pingResult ? (
                    <p className="text-xs text-[var(--jr-text-3)] italic">{t("integr.pplx_ping_hint")}</p>
                ) : (
                    <div className="rounded-lg border border-[#22c55e]/25 bg-[#22c55e]/[0.06] p-3 text-xs text-[var(--jr-text-2)] inline-flex items-start gap-2"
                        data-testid="perplexity-ping-result">
                        <CheckCircle2 size={14} className="text-[#22c55e] mt-0.5 shrink-0" />
                        <div>
                            <div className="text-white">{pingResult.model} → &quot;{pingResult.echo}&quot;</div>
                            <div className="text-[var(--jr-text-3)]">{t("integr.pplx_ping_ok")}</div>
                        </div>
                    </div>
                )}
            </SubSection>

            <SubSection title={`3. ${t("integr.pplx_probe_title")}`} testid="perplexity-probe-section">
                <p className="text-[11px] text-[var(--jr-text-3)] mb-2">
                    {t("integr.pplx_probe_sub")} <span className="text-[var(--jr-amber)]">{t("integr.pplx_probe_cost")}</span>
                </p>
                <div className="grid grid-cols-3 gap-2">
                    <input value={probe.target_sector || ""} onChange={(e) => setProbe({ ...probe, target_sector: e.target.value })}
                        placeholder={t("integr.sector_ph")} data-testid="perplexity-probe-sector"
                        className="bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-xs text-white outline-none focus:border-[var(--jr-brand)]" />
                    <input value={probe.target_country || ""} onChange={(e) => setProbe({ ...probe, target_country: e.target.value })}
                        placeholder={t("integr.country_ph")} data-testid="perplexity-probe-country"
                        className="bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-xs text-white outline-none focus:border-[var(--jr-brand)]" />
                    <input value={probe.target_role || ""} onChange={(e) => setProbe({ ...probe, target_role: e.target.value })}
                        placeholder={t("integr.role_ph")} data-testid="perplexity-probe-role"
                        className="bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-xs text-white outline-none focus:border-[var(--jr-brand)]" />
                </div>
                <button onClick={runProbe} disabled={probeBusy || !cfg.configured} data-testid="perplexity-probe-run"
                    className="mt-2 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[var(--jr-brand)] text-black text-xs font-medium hover:bg-[var(--jr-brand-2)] disabled:opacity-50">
                    {probeBusy ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />} {t("integr.pplx_probe_run")}
                </button>
                {probeResult && probeResult.length > 0 && (
                    <ul className="mt-3 space-y-2" data-testid="perplexity-probe-result">
                        {probeResult.map((s, i) => (
                            <li key={i} className="rounded-lg border border-white/8 bg-[var(--jr-surface-2)]/40 p-3 text-xs">
                                <div className="flex items-center justify-between">
                                    <span className="text-white font-medium">{s.company_name}</span>
                                    <span className="text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)]">{s.signal_type}</span>
                                </div>
                                <div className="mt-1 text-[var(--jr-text-2)]">{s.signal_title}</div>
                                <p className="mt-1 text-[11px] text-[var(--jr-text-3)] line-clamp-2">{s.signal_detail}</p>
                                {(s.source_urls || []).length > 0 && (
                                    <div className="mt-2 flex flex-wrap gap-1">
                                        {(s.source_urls || []).slice(0, 4).map((c, idx) => (
                                            <a key={idx} href={c.url} target="_blank" rel="noreferrer"
                                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[var(--jr-info)]/[0.08] border border-[var(--jr-info)]/25 text-[var(--jr-info)] hover:bg-[var(--jr-info)]/[0.16] text-[10px] font-mono">
                                                <Link2 size={9} /> {hostOf(c.url)}
                                            </a>
                                        ))}
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </SubSection>
        </div>
    );
}

export default PerplexityVault;
